/**
 * WorkspaceClosedOverlay — the boot-level refusal / suite-navigation screen.
 *
 * Non-dismissible full-screen overlay shown when Central refuses entry:
 *   WORKSPACE_CLOSED (400/409)            → "Workspace Closed"
 *   MEMBERSHIP_INACTIVE / _REVOKED (403)  → "Workspace access removed"
 *   APP_ACCESS_DENIED / other 403         → "Access denied"
 * The reason is carried in beheardAuthExt.bootDenial (auth/bootDenial.ts); it
 * used to be "Workspace Closed" for all three (BH-06).
 *
 * z-[9998] — below CentralUnavailableOverlay (z-[9999]).
 * Offers: switch workspace (via useWorkspace — classified), "Back to LevelUp"
 * (`${CENTRAL}/home`, suite contract 1.3), and a FULL sign-out
 * (services/oidc/appSignOut — local clear + Central RP logout).
 */

import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import {
  selectCurrentCentralWorkspace,
  selectOtherWorkspaces,
  selectBootDenial,
  selectTokenWorkspaceId,
  type BootDenialReason,
} from '../../store/slices/centralAuthSlice';
import { selectBootClosedWorkspaceResponse } from '../../store/slices/bootContextSlice';
import { CENTRAL_URL } from '../../config/centralAuth';
import { useWorkspace } from '../../hooks/useWorkspace';
import { appSignOut } from '../../services/oidc/appSignOut';

interface ReachableWorkspace {
  id: string;
  name: string;
  role?: string;
}

/** Overlay copy per refusal reason (BH-06). `workspaceName` may be unknown. */
export function denialCopy(
  reason: BootDenialReason,
  workspaceName: string | null,
): { title: string; body: string } {
  switch (reason) {
    case 'app_access_denied':
      return {
        title: 'Access denied',
        body: workspaceName
          ? `You don't have access to BeHeard in ${workspaceName}. Ask your workspace owner or an admin to assign it to you, or switch to another workspace.`
          : "You don't have access to BeHeard in this workspace. Ask your workspace owner or an admin to assign it to you, or switch to another workspace.",
      };
    case 'membership':
      return {
        title: 'Workspace access removed',
        body: workspaceName
          ? `Your membership in ${workspaceName} is no longer active. Switch to another workspace or go back to LevelUp.`
          : 'Your membership in this workspace is no longer active. Switch to another workspace or go back to LevelUp.',
      };
    case 'workspace_closed':
    default:
      return {
        title: 'Workspace Closed',
        body: workspaceName
          ? `The workspace ${workspaceName} has been closed.`
          : 'Your current workspace has been closed.',
      };
  }
}

/** Central's suite home ("Back to LevelUp", suite contract 1.3), or null if unconfigured. */
export function backToLevelUpUrl(centralUrl: string = CENTRAL_URL): string | null {
  if (!centralUrl) return null;
  try {
    return new URL('/home', centralUrl).toString();
  } catch {
    return null;
  }
}

const selectTokenWorkspaces = (state: RootState) => state.centralAuth.user?.workspaces;

export function WorkspaceClosedOverlay() {
  const dispatch = useDispatch<AppDispatch>();
  const currentWorkspace = useSelector(selectCurrentCentralWorkspace);
  const bootOtherWorkspaces = useSelector(selectOtherWorkspaces);
  const closedResponse = useSelector(selectBootClosedWorkspaceResponse);
  const tokenWorkspaces = useSelector(selectTokenWorkspaces);
  const tokenWorkspaceId = useSelector(selectTokenWorkspaceId);
  const denial = useSelector(selectBootDenial);
  const { switchWorkspace } = useWorkspace();
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  // The workspaces still reachable. The shared reducer EMPTIES
  // bootContext.otherWorkspaces when it raises this overlay and keeps Central's
  // list in closedWorkspaceResponse — reading only the former meant the overlay
  // always said "no other workspaces". Fall back to the token's workspace list.
  const fromRefusal = (closedResponse?.otherWorkspaces ?? []) as ReachableWorkspace[];
  const fromToken = ((tokenWorkspaces ?? []) as ReachableWorkspace[]).filter((ws) => ws.id !== tokenWorkspaceId);
  const otherWorkspaces: ReachableWorkspace[] =
    fromRefusal.length > 0 ? fromRefusal : bootOtherWorkspaces.length > 0 ? bootOtherWorkspaces : fromToken;

  const reason: BootDenialReason = denial?.reason ?? 'workspace_closed';
  const copy = denialCopy(reason, currentWorkspace?.name ?? null);
  const homeUrl = backToLevelUpUrl();

  // One switch path for the whole app: useWorkspace classifies every Central
  // answer (this overlay used to carry its own unclassified copy of the flow).
  const handleSwitchWorkspace = async (workspaceId: string) => {
    if (switchingId) return;
    setSwitchingId(workspaceId);
    setError('');
    const result = await switchWorkspace(workspaceId);
    if (!result.success) {
      setError(result.error || 'Failed to switch workspace. Please try again.');
    }
    setSwitchingId(null);
  };

  // Full sign-out (BH-03): clear this tab AND end Central's session. A local
  // `centralLogout()` alone sent the user to /login, which re-authorized over
  // the still-live SSO session straight back into this refused workspace.
  const handleLogout = () => {
    appSignOut(dispatch);
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900" data-testid="boot-denial-title">{copy.title}</h2>
          <p className="mt-2 text-sm text-gray-600" data-testid="boot-denial-body">
            {copy.body}
          </p>
        </div>

        {otherWorkspaces.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Switch to another workspace:</p>
            <div className="space-y-2">
              {otherWorkspaces.map((ws) => (
                <button
                  key={ws.id}
                  onClick={() => handleSwitchWorkspace(ws.id)}
                  disabled={switchingId !== null}
                  className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors disabled:opacity-50"
                >
                  <span className="font-medium text-gray-900">{ws.name}</span>
                  {ws.role && <span className="ml-2 text-xs text-gray-500 capitalize">{ws.role}</span>}
                  {switchingId === ws.id && (
                    <span className="ml-2 inline-block animate-spin h-3 w-3 border-2 border-primary-600 border-t-transparent rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {otherWorkspaces.length === 0 && (
          <p className="text-sm text-gray-500 text-center mb-6">
            You have no other workspaces available. Contact your administrator.
          </p>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg text-center">
            {error}
          </div>
        )}

        {homeUrl && (
          <a
            href={homeUrl}
            className="mb-3 block w-full px-4 py-2 text-center text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            data-testid="back-to-levelup"
          >
            Back to LevelUp
          </a>
        )}

        <button
          onClick={handleLogout}
          className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
